import React, { useState } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { priceChangeRequestService, PriceChangeRequest } from '../services/priceChangeRequestService';
import { useAuth } from '../contexts/AuthContext';

const PriceChangeRequestsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, hasPermission } = useAuth();
  const canAccessPriceChange = isSuperAdmin || hasPermission('price_change');
  const [statusFilter, setStatusFilter] = useState<'PENDING' | 'APPROVED' | 'REJECTED' | ''>('');
  const [publisherFilter, setPublisherFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState<'approve' | 'reject'>('approve');
  const [modalRequest, setModalRequest] = useState<PriceChangeRequest | null>(null);
  const [rejectionReasonInput, setRejectionReasonInput] = useState('');

  // Fetch requests
  const { data: allRequests = [], isLoading, refetch } = useQuery({
    queryKey: ['price-change-requests', statusFilter],
    queryFn: () => priceChangeRequestService.getRequests(statusFilter || undefined),
    enabled: canAccessPriceChange,
  });

  // Get unique publishers from requests (only those who have made requests)
  const publishersWithRequests = React.useMemo(() => {
    const publisherMap = new Map();
    allRequests.forEach(req => {
      if (req.publisher && !publisherMap.has(req.publisherId)) {
        publisherMap.set(req.publisherId, {
          id: req.publisherId,
          publisherName: req.publisher.publisherName,
          email: req.publisher.email,
        });
      }
    });
    return Array.from(publisherMap.values());
  }, [allRequests]);

  // Filter requests by publisher on frontend
  const filteredRequests = publisherFilter
    ? allRequests.filter(req => req.publisherId === publisherFilter)
    : allRequests;

  // Pagination
  const totalCount = filteredRequests.length;
  const requests = filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: priceChangeRequestService.approveRequest.bind(priceChangeRequestService),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-change-requests'] });
      queryClient.invalidateQueries({ queryKey: ['pending-price-requests-count'] });
      queryClient.invalidateQueries({ queryKey: ['guest-sites'] });
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      priceChangeRequestService.rejectRequest(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-change-requests'] });
      queryClient.invalidateQueries({ queryKey: ['pending-price-requests-count'] });
    },
  });


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const pendingCount = requests.filter(r => r.status === 'PENDING').length;

  if (!canAccessPriceChange) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Access denied. You do not have permission to access Cost Update.</Alert>
      </Box>
    );
  }

  const openModal = (request: PriceChangeRequest, action: 'approve' | 'reject') => {
    setModalRequest(request);
    setModalAction(action);
    setModalOpen(true);
    setRejectionReasonInput('');
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalRequest(null);
    setRejectionReasonInput('');
  };

  const confirmAction = () => {
    if (!modalRequest) return;
    if (modalAction === 'approve') {
      approveMutation.mutate(modalRequest.id);
    } else {
      rejectMutation.mutate({
        id: modalRequest.id,
        reason: rejectionReasonInput.trim() || undefined,
      });
    }
    closeModal();
  };

  return (
    <Box className="page">
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes badgePulse { 0%, 100% { box-shadow: 0 3px 10px rgba(234,88,12,.3); } 50% { box-shadow: 0 4px 18px rgba(234,88,12,.5); } }
        
        .page { padding: 22px; display: flex; flex-direction: column; gap: 16px; }
        
        .ph { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
        .ph-title { font-size: 19px; font-weight: 700; letter-spacing: -0.4px; color: #111827; }
        .ph-sub { font-size: 13px; color: #6B7280; margin-top: 3px; }
        .ph-right { display: flex; align-items: center; gap: 8px; padding-top: 3px; flex-shrink: 0; }
        
        .pending-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 6px 14px; border-radius: 99px;
          background: linear-gradient(135deg, #D97706, #EA580C);
          color: #fff; font-size: 12.5px; font-weight: 700;
          box-shadow: 0 3px 10px rgba(234,88,12,.3);
          animation: badgePulse 2.4s ease-in-out infinite;
        }
        .pending-badge svg { width: 12px; height: 12px; }
        
        .refresh-btn {
          width: 34px; height: 34px; border-radius: 6px;
          border: 1px solid #E5E7EB; background: #FFFFFF;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.14s;
        }
        .refresh-btn:hover { background: #F9FAFB; border-color: #D1D5DB; }
        .refresh-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .refresh-btn svg { width: 15px; height: 15px; color: #6B7280; }
        .refresh-btn.spinning svg { animation: spin 0.6s linear 1; }
        
        .filter-card {
          background: #FFFFFF; border: 1px solid #E5E7EB;
          border-radius: 8px; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          padding: 13px 16px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
        }
        .fsel {
          appearance: none; border: 1px solid #E5E7EB; border-radius: 6px;
          padding: 7px 28px 7px 11px; font-family: inherit; font-size: 13px;
          color: #374151; background: #F9FAFB; outline: none; cursor: pointer;
          transition: all 0.14s; min-width: 155px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 9px center;
        }
        .fsel:focus { border-color: #4F46E5; background-color: #FFFFFF; box-shadow: 0 0 0 3px rgba(79,70,229,0.08); }
        .fcount { font-size: 12px; color: #9CA3AF; margin-left: auto; }
        
        .table-card {
          background: #FFFFFF; border: 1px solid #E5E7EB;
          border-radius: 8px; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          overflow: hidden; animation: fadeUp 0.22s ease forwards;
        }
        .t-toolbar {
          display: flex; align-items: center; justify-content: space-between;
          padding: 10px 16px; border-bottom: 1px solid #F3F4F6; background: #FAFAFA;
        }
        .tcnt { font-size: 12.5px; color: #6B7280; }
        .tcnt b { color: #111827; font-weight: 600; }
        .tscroll { overflow-x: auto; }
        table { width: 100%; border-collapse: collapse; min-width: 1080px; }
        thead th {
          text-align: left; font-size: 10.5px; font-weight: 600; color: #9CA3AF;
          letter-spacing: 0.5px; text-transform: uppercase; padding: 9px 14px;
          border-bottom: 1px solid #E5E7EB; background: #FAFAFA;
          white-space: nowrap; position: sticky; top: 0; z-index: 10;
        }
        thead th:first-child { padding-left: 18px; }
        thead th.ar { text-align: right; }
        thead th:last-child { text-align: right; padding-right: 18px; }
        tbody tr { transition: background 0.08s; border-bottom: 1px solid #F3F4F6; }
        tbody tr:last-child { border-bottom: none; }
        tbody tr:hover { background: #FAFBFF; }
        tbody tr.rp { background: #FFFBEB; }
        tbody tr.rp:hover { background: #FEF3C7; }
        tbody td { padding: 13px 14px; font-size: 12.5px; vertical-align: middle; white-space: nowrap; }
        tbody td:first-child { padding-left: 18px; }
        tbody td:last-child { padding-right: 18px; }
        tbody td.ar { text-align: right; }
        
        .c-url { color: #4F46E5; font-weight: 500; text-decoration: none; font-size: 13px; display: block; max-width: 215px; overflow: hidden; text-overflow: ellipsis; }
        .c-url:hover { text-decoration: underline; }
        .c-cat { font-size: 10.5px; color: #9CA3AF; margin-top: 2px; letter-spacing: 0.3px; font-weight: 500; text-transform: uppercase; }
        .c-pn { font-size: 13px; font-weight: 500; color: #111827; }
        .c-pe { font-size: 11px; color: #6B7280; margin-top: 2px; }
        .c-price { font-family: 'JetBrains Mono', monospace; font-size: 13.5px; font-weight: 600; color: #111827; }
        .c-date { font-size: 12px; color: #374151; }
        .c-reason { font-size: 12px; color: #6B7280; max-width: 130px; overflow: hidden; text-overflow: ellipsis; display: block; }
        
        .chg { display: flex; align-items: center; justify-content: flex-end; gap: 3px; font-family: 'JetBrains Mono', monospace; font-size: 13.5px; font-weight: 700; }
        .chg svg { width: 12px; height: 12px; flex-shrink: 0; }
        .chg-abs { display: block; text-align: right; font-family: 'JetBrains Mono', monospace; font-size: 11px; margin-top: 2px; }
        .cu { color: #059669; }
        .cd { color: #DC2626; }
        
        .badge { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 99px; font-size: 11.5px; font-weight: 700; white-space: nowrap; }
        .badge::before { content: ''; width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
        .b-pend { background: #FFFBEB; color: #B45309; border: 1px solid #FDE68A; }
        .b-pend::before { background: #D97706; }
        .b-appr { background: #ECFDF5; color: #047857; border: 1px solid #A7F3D0; }
        .b-appr::before { background: #059669; }
        .b-rej { background: #FEF2F2; color: #B91C1C; border: 1px solid #FECACA; }
        .b-rej::before { background: #DC2626; }
        .badge-sub { font-size: 10.5px; color: #9CA3AF; margin-top: 3px; display: block; font-weight: 400; }
        .badge-sub-mono { font-size: 10px; color: #9CA3AF; display: block; font-family: 'JetBrains Mono', monospace; margin-top: 1px; }
        
        .act-row { display: inline-flex; align-items: center; gap: 4px; justify-content: flex-end; }
        .ab { width: 30px; height: 30px; border-radius: 6px; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.15s; }
        .ab svg { width: 13px; height: 13px; }
        .ab-ok { background: #ECFDF5; color: #059669; }
        .ab-ok:hover { background: #059669; color: #fff; box-shadow: 0 2px 8px rgba(5,150,105,0.35); transform: scale(1.08); }
        .ab-x { background: #FEF2F2; color: #DC2626; }
        .ab-x:hover { background: #DC2626; color: #fff; box-shadow: 0 2px 8px rgba(220,38,38,0.3); transform: scale(1.08); }
        .act-done { font-size: 11.5px; color: #9CA3AF; font-style: italic; white-space: nowrap; }
        
        .pag { display: flex; align-items: center; justify-content: flex-end; gap: 10px; padding: 9px 16px; border-top: 1px solid #F3F4F6; background: #FAFAFA; }
        .pag-info { font-size: 12px; color: #9CA3AF; }
        .pag-rpp { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #374151; }
        .pag-rpp select { border: 1px solid #E5E7EB; background: #FFFFFF; border-radius: 5px; padding: 3px 6px; font-family: inherit; font-size: 12px; outline: none; cursor: pointer; }
        .pag-btns { display: flex; gap: 3px; }
        .pagb { width: 26px; height: 26px; border-radius: 5px; border: 1px solid #E5E7EB; background: #FFFFFF; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.1s; }
        .pagb:hover { background: #F9FAFB; }
        .pagb:disabled { opacity: 0.5; cursor: not-allowed; }
        .pagb svg { width: 12px; height: 12px; color: #374151; }
        
        .empty-wrap { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 72px 24px; gap: 12px; text-align: center; }
        .empty-ico { width: 60px; height: 60px; background: #F9FAFB; border-radius: 14px; display: flex; align-items: center; justify-content: center; border: 1px solid #E5E7EB; margin-bottom: 4px; }
        .empty-ico svg { width: 26px; height: 26px; color: #D1D5DB; }
        .empty-title { font-size: 15px; font-weight: 600; color: #111827; letter-spacing: -0.2px; }
        .empty-sub { font-size: 13px; color: #6B7280; max-width: 300px; line-height: 1.65; }
        
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); backdrop-filter: blur(3px); z-index: 900; display: flex; align-items: center; justify-content: center; opacity: 0; pointer-events: none; transition: opacity 0.18s; }
        .modal-overlay.open { opacity: 1; pointer-events: all; }
        .modal { background: #FFFFFF; border-radius: 12px; box-shadow: 0 20px 50px rgba(0,0,0,0.12), 0 8px 16px rgba(0,0,0,0.07); width: 100%; max-width: 470px; overflow: hidden; transform: translateY(10px) scale(0.97); transition: transform 0.22s cubic-bezier(0.34,1.56,0.64,1); }
        .modal-overlay.open .modal { transform: translateY(0) scale(1); }
        .modal-head { padding: 22px 22px 0; display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
        .modal-icon { width: 46px; height: 46px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .mi-ok { background: #ECFDF5; border: 1.5px solid #A7F3D0; }
        .mi-ok svg { width: 22px; height: 22px; color: #059669; }
        .mi-x { background: #FEF2F2; border: 1.5px solid #FECACA; }
        .mi-x svg { width: 22px; height: 22px; color: #DC2626; }
        .modal-close { width: 28px; height: 28px; border-radius: 5px; border: none; background: none; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #9CA3AF; transition: all 0.1s; margin-top: -2px; }
        .modal-close:hover { background: #F9FAFB; color: #111827; }
        .modal-close svg { width: 14px; height: 14px; }
        .modal-body { padding: 16px 22px 20px; }
        .m-title { font-size: 16px; font-weight: 700; color: #111827; letter-spacing: -0.25px; margin-bottom: 6px; }
        .m-desc { font-size: 13px; color: #6B7280; line-height: 1.6; }
        
        .m-banner { display: flex; align-items: center; gap: 0; background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 8px; overflow: hidden; margin-top: 14px; }
        .m-price-col { flex: 1; padding: 12px 16px; text-align: center; }
        .m-price-col + .m-price-col { border-left: 1px solid #E5E7EB; }
        .mpc-lbl { font-size: 10.5px; font-weight: 600; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px; }
        .mpc-val { font-family: 'JetBrains Mono', monospace; font-size: 18px; font-weight: 700; color: #111827; }
        .mpc-val.green { color: #059669; }
        .mpc-val.red { color: #DC2626; }
        .m-arrow { width: 36px; display: flex; align-items: center; justify-content: center; background: #F9FAFB; flex-shrink: 0; }
        .m-arrow svg { width: 16px; height: 16px; color: #9CA3AF; }
        .m-change { flex: 1; padding: 12px 16px; text-align: center; border-left: 1px solid #E5E7EB; }
        .mpc-chg { font-family: 'JetBrains Mono', monospace; font-size: 16px; font-weight: 700; }
        
        .m-card { margin-top: 18px; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 8px; overflow: hidden; }
        .m-card-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0; }
        .m-cell { padding: 12px 14px; border-bottom: 1px solid #F3F4F6; }
        .m-cell:nth-child(odd) { border-right: 1px solid #F3F4F6; }
        .m-cell:nth-last-child(-n+2) { border-bottom: none; }
        .m-lbl { font-size: 10px; font-weight: 700; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 5px; }
        .m-val { font-family: 'JetBrains Mono', monospace; font-size: 14px; font-weight: 700; color: #111827; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .m-val-url { font-family: 'Inter', sans-serif; font-size: 12px; color: #4F46E5; font-weight: 500; }
        .m-val-pub { font-family: 'Inter', sans-serif; font-size: 13px; color: #111827; font-weight: 500; }
        
        .m-reason-field { margin-top: 14px; }
        .m-reason-field label { display: block; font-size: 11px; font-weight: 600; color: #6B7280; margin-bottom: 6px; }
        .m-reason-field textarea { width: 100%; border: 1px solid #E5E7EB; border-radius: 6px; padding: 8px 10px; font-family: inherit; font-size: 13px; color: #111827; outline: none; resize: vertical; min-height: 70px; }
        .m-reason-field textarea:focus { border-color: #4F46E5; box-shadow: 0 0 0 3px rgba(79,70,229,0.08); }
        
        .modal-footer { display: flex; align-items: center; justify-content: flex-end; gap: 8px; padding: 14px 22px; border-top: 1px solid #F3F4F6; background: #FAFAFA; }
        .btn { display: inline-flex; align-items: center; gap: 6px; padding: 7px 13px; border-radius: 6px; font-family: inherit; font-size: 12.5px; font-weight: 500; cursor: pointer; border: none; transition: all 0.14s; white-space: nowrap; line-height: 1; }
        .btn svg { width: 13px; height: 13px; flex-shrink: 0; }
        .btn-secondary { background: #FFFFFF; color: #374151; border: 1px solid #E5E7EB; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .btn-secondary:hover { background: #F9FAFB; color: #111827; }
        .btn-success { background: #059669; color: #fff; }
        .btn-success:hover { background: #047857; }
        .btn-danger { background: #DC2626; color: #fff; }
        .btn-danger:hover { background: #B91C1C; }
      `}</style>

      {/* Header */}
      <div className="ph">
        <div>
          <h1 className="ph-title">Price Change Requests</h1>
          <p className="ph-sub">Review and manage publisher price change requests</p>
        </div>
        <div className="ph-right">
          {pendingCount > 0 && (
            <div className="pending-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>{pendingCount} Pending</span>
            </div>
          )}
          <button 
            className={`refresh-btn ${isLoading ? 'spinning' : ''}`}
            onClick={() => refetch()} 
            disabled={isLoading}
            title="Refresh"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filter-card">
        <select 
          className="fsel" 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value as any)}
        >
          <option value="">Status</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <select 
          className="fsel" 
          value={publisherFilter} 
          onChange={(e) => setPublisherFilter(e.target.value)}
          style={{ minWidth: '200px' }}
        >
          <option value="">Publisher</option>
          {publishersWithRequests.map((publisher) => (
            <option key={publisher.id} value={publisher.id}>
              {publisher.publisherName} {publisher.email ? `(${publisher.email})` : ''}
            </option>
          ))}
        </select>
        <span className="fcount">{totalCount} result{totalCount !== 1 ? 's' : ''}</span>
      </div>

      {/* Requests Table */}
      <div className="table-card">
        <div className="t-toolbar">
          <span className="tcnt">Showing <b>{requests.length}</b> request{requests.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="tscroll">
          <table>
            <thead>
              <tr>
                <th style={{ minWidth: '210px' }}>Site URL</th>
                <th style={{ minWidth: '155px' }}>Publisher</th>
                <th className="ar" style={{ minWidth: '115px' }}>Current Price</th>
                <th className="ar" style={{ minWidth: '120px' }}>Requested Price</th>
                <th className="ar" style={{ minWidth: '140px' }}>Change</th>
                <th style={{ minWidth: '120px' }}>Reason</th>
                <th style={{ minWidth: '150px' }}>Requested Date</th>
                <th style={{ minWidth: '155px' }}>Status</th>
                <th style={{ minWidth: '95px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '40px' }}>
                    <CircularProgress size={40} />
                  </td>
                </tr>
              ) : requests.length === 0 ? null : (
                requests.map((request) => {
                  const priceChange = priceChangeRequestService.calculatePriceChange(
                    request.currentPrice,
                    request.requestedPrice
                  );
                  const sign = priceChange.isIncrease ? '+' : '';
                  const cls = priceChange.isIncrease ? 'cu' : 'cd';
                  return (
                    <tr key={request.id} className={request.status === 'PENDING' ? 'rp' : ''}>
                      <td>
                        <a href="#" className="c-url" title={request.site?.site_url}>
                          {request.site?.site_url}
                        </a>
                        <div className="c-cat">{request.site?.category}</div>
                      </td>
                      <td>
                        <div className="c-pn">{request.publisher?.publisherName}</div>
                        {request.publisher?.email && (
                          <div className="c-pe">{request.publisher.email}</div>
                        )}
                      </td>
                      <td className="ar">
                        <span className="c-price">${request.currentPrice.toFixed(2)}</span>
                      </td>
                      <td className="ar">
                        <span className="c-price">${request.requestedPrice.toFixed(2)}</span>
                      </td>
                      <td className="ar">
                        <div className={`chg ${cls}`}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            {priceChange.isIncrease ? (
                              <polyline points="18 15 12 9 6 15"/>
                            ) : (
                              <polyline points="6 9 12 15 18 9"/>
                            )}
                          </svg>
                          {sign}{priceChange.percentage.toFixed(1)}%
                        </div>
                        <span className={`chg-abs ${cls}`}>{sign}${Math.abs(priceChange.amount).toFixed(2)}</span>
                      </td>
                      <td>
                        <span className="c-reason" title={request.reason || ''}>
                          {request.reason || '–'}
                        </span>
                      </td>
                      <td>
                        <span className="c-date">{formatDate(request.requestedAt)}</span>
                      </td>
                      <td>
                        {request.status === 'PENDING' && (
                          <span className="badge b-pend">Pending Review</span>
                        )}
                        {request.status === 'APPROVED' && (
                          <div>
                            <span className="badge b-appr">Approved</span>
                            {request.reviewer && (
                              <>
                                <span className="badge-sub">
                                  {request.reviewer.firstName} {request.reviewer.lastName}
                                </span>
                                {request.reviewedAt && (
                                  <span className="badge-sub-mono">{formatDate(request.reviewedAt)}</span>
                                )}
                              </>
                            )}
                          </div>
                        )}
                        {request.status === 'REJECTED' && (
                          <div>
                            <span className="badge b-rej">Rejected</span>
                            {request.reviewer && (
                              <span className="badge-sub">
                                {request.reviewer.firstName} {request.reviewer.lastName}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {request.status === 'PENDING' ? (
                          <div className="act-row">
                            <button 
                              className="ab ab-ok" 
                              title="Approve" 
                              onClick={() => openModal(request, 'approve')}
                              disabled={approveMutation.isPending}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                            </button>
                            <button 
                              className="ab ab-x" 
                              title="Reject" 
                              onClick={() => openModal(request, 'reject')}
                              disabled={rejectMutation.isPending}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <span className="act-done">
                            {request.status === 'APPROVED' ? '✓ Approved' : '✗ Rejected'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!isLoading && requests.length === 0 && (
          <div className="empty-wrap">
            <div className="empty-ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
            <div className="empty-title">No price change requests found</div>
            <div className="empty-sub">No requests match your current filters. Try changing the status or publisher filter.</div>
          </div>
        )}
        {totalCount > 0 && (
          <div className="pag">
            <div className="pag-rpp">
              Rows per page: 
              <select 
                value={rowsPerPage} 
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <span className="pag-info">
              {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, totalCount)} of {totalCount}
            </span>
            <div className="pag-btns">
              <button 
                className="pagb" 
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
              <button 
                className="pagb" 
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * rowsPerPage >= totalCount}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      <div className={`modal-overlay ${modalOpen ? 'open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
        <div className="modal">
          <div className="modal-head">
            <div className={`modal-icon ${modalAction === 'approve' ? 'mi-ok' : 'mi-x'}`}>
              {modalAction === 'approve' ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              )}
            </div>
            <button className="modal-close" onClick={closeModal}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <div className="modal-body">
            <div className="m-title">
              {modalAction === 'approve' ? 'Approve Price Change?' : 'Reject Price Change?'}
            </div>
            <div className="m-desc">
              {modalAction === 'approve'
                ? 'This will approve the price change and update the site pricing in the system.'
                : 'This will reject the price change. The current price will remain unchanged.'}
            </div>
            {modalRequest && (
              <>
                {/* Price comparison banner */}
                <div className="m-banner">
                  <div className="m-price-col">
                    <div className="mpc-lbl">Current Price</div>
                    <div className="mpc-val">${modalRequest.currentPrice.toFixed(2)}</div>
                  </div>
                  <div className="m-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                      <polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </div>
                  <div className="m-price-col">
                    <div className="mpc-lbl">Requested Price</div>
                    <div className={`mpc-val ${modalRequest.requestedPrice >= modalRequest.currentPrice ? 'green' : 'red'}`}>
                      ${modalRequest.requestedPrice.toFixed(2)}
                    </div>
                  </div>
                  <div className="m-change">
                    <div className="mpc-lbl">Change</div>
                    <div className={`mpc-chg ${modalRequest.requestedPrice >= modalRequest.currentPrice ? 'cu' : 'cd'}`}>
                      {(() => {
                        const priceChange = priceChangeRequestService.calculatePriceChange(
                          modalRequest.currentPrice,
                          modalRequest.requestedPrice
                        );
                        const sign = priceChange.isIncrease ? '+' : '';
                        return `${sign}${priceChange.percentage.toFixed(1)}% (${sign}$${Math.abs(priceChange.amount).toFixed(2)})`;
                      })()}
                    </div>
                  </div>
                </div>
                {/* Info card */}
                <div className="m-card">
                  <div className="m-card-row">
                    <div className="m-cell">
                      <div className="m-lbl">Site URL</div>
                      <div className="m-val m-val-url">{modalRequest.site?.site_url}</div>
                    </div>
                    <div className="m-cell">
                      <div className="m-lbl">Publisher</div>
                      <div className="m-val m-val-pub">{modalRequest.publisher?.publisherName}</div>
                    </div>
                    <div className="m-cell">
                      <div className="m-lbl">Reason</div>
                      <div className="m-val" style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif', fontWeight: 400, color: '#374151' }}>
                        {modalRequest.reason || '–'}
                      </div>
                    </div>
                    <div className="m-cell">
                      <div className="m-lbl">Requested</div>
                      <div className="m-val" style={{ fontSize: '11px', fontWeight: 400, color: '#6B7280' }}>
                        {formatDate(modalRequest.requestedAt)}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Rejection reason field (only for reject action) */}
                {modalAction === 'reject' && (
                  <div className="m-reason-field">
                    <label>Rejection Reason (Optional)</label>
                    <textarea
                      value={rejectionReasonInput}
                      onChange={(e) => setRejectionReasonInput(e.target.value)}
                      placeholder="Explain why this request is being rejected..."
                      disabled={rejectMutation.isPending}
                    />
                  </div>
                )}
              </>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={closeModal}>
              Cancel
            </button>
            <button 
              className={`btn ${modalAction === 'approve' ? 'btn-success' : 'btn-danger'}`}
              onClick={confirmAction}
              disabled={approveMutation.isPending || rejectMutation.isPending}
            >
              {modalAction === 'approve' ? (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Approve
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Reject
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default PriceChangeRequestsPage;
